/**
 * BillingPage
 * Subscription management and payment
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { signOut, getCurrentUser } from '@/services/auth.service';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, functions } from '@/services/firebase';
import { useRedirectWhenSubscribed } from '@/hooks/useRedirectWhenSubscribed';
import { getAuthErrorCode } from '@/utils/authErrors';
import './styles/BillingPage.css';

// Stripe Configuration
const STRIPE_CONFIG = {
  publishableKey:
    'pk_live_51SoxwSK3uIQgKnZ0YMnYKVYaxMxbTYRhIfcAPg7JVpbhV1QG0vnGccCNB6qlsjXODDnufG8datg68vNaSHWOBJPI00gkR2BGFu',
  monthlyPriceId: 'price_1SuLBzK3uIQgKnZ0PToDP5lS',
  yearlyPriceId: 'price_1SuLBzK3uIQgKnZ03wA3vKTU',
};

export function BillingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgMissing, setOrgMissing] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');

  // Checkout only needs the org doc to exist — createCheckoutSession creates
  // a missing Stripe customer on demand, so there is nothing else to wait for.
  const checkoutDisabled = loading !== null || orgLoading || orgMissing;

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleSubscribe = async (plan: 'monthly' | 'yearly') => {
    if (checkoutDisabled) {
      return;
    }

    setLoading(plan);
    setSubscribeError('');

    try {
      // Get Stripe price ID
      const priceId =
        plan === 'monthly'
          ? STRIPE_CONFIG.monthlyPriceId
          : STRIPE_CONFIG.yearlyPriceId;

      if (!priceId) {
        throw new Error('Stripe price ID not configured');
      }

      // Call Cloud Function to create checkout session
      const createCheckoutSession = httpsCallable(
        functions,
        'createCheckoutSession'
      );

      const result = await createCheckoutSession({
        priceId,
        plan,
      });

      const { url } = result.data as { url: string };

      // Redirect to Stripe Checkout
      if (!url) {
        throw new Error('No checkout URL returned');
      }

      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      // Already subscribed — the backend guard refuses a second checkout
      if (getAuthErrorCode(error) === 'functions/failed-precondition') {
        navigate('/booth', { replace: true });
        return;
      }
      setSubscribeError(
        'We couldn’t start the checkout. Please try again — if it keeps happening, email hello@sundaybestapp.com.'
      );
    } finally {
      setLoading(null);
    }
  };

  // If a subscription is already active, this page has nothing to sell
  useRedirectWhenSubscribed();

  // Watch the org doc: its absence is the one state checkout can't recover
  // from (a signup that never finished).
  useEffect(() => {
    const user = getCurrentUser();

    if (!user) {
      setOrgLoading(false);
      return;
    }

    const orgRef = doc(db, 'organizations', user.uid);
    const unsubscribe = onSnapshot(
      orgRef,
      (snapshot) => {
        setOrgMissing(!snapshot.exists());
        setOrgLoading(false);
      },
      (err) => {
        console.error('Error listening for org updates:', err);
        setOrgLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <div className="billing-page">
      {/* Logout Button */}
      <button onClick={handleLogout} className="logout-button">
        ← Logout
      </button>

      <div className="billing-container">
        <div className="billing-header">
          <h1 className="billing-logo">Sunday Best</h1>
          <h2 className="billing-title">Choose Your Plan 🎉</h2>
          <p className="billing-subtitle">
            Start your 30-day free trial today
          </p>
        </div>

        {orgMissing && !orgLoading && (
          <div className="billing-error">
            <p>
              We couldn’t find your account setup — it looks like signup didn’t
              finish. Please sign out and sign in again to complete it.
            </p>
            <button onClick={handleLogout} className="billing-error-button">
              Sign Out
            </button>
            <p className="billing-error-support">
              Need a hand? Email{' '}
              <a href="mailto:hello@sundaybestapp.com">
                hello@sundaybestapp.com
              </a>
            </p>
          </div>
        )}

        {subscribeError && <p className="billing-error">{subscribeError}</p>}
        <div className="pricing-cards">
          <div className="billing-card">
            <h3>Monthly</h3>
            <div className="billing-price">
              <span className="currency">$</span>
              <span className="amount">29</span>
              <span className="period">/month</span>
            </div>
            <ul className="billing-features">
              <li>✓ Unlimited photos</li>
              {/* <li>✓ Custom branding</li> */}
              <li>✓ Email delivery</li>
              <li>✓ Custom questionnaires</li>
              <li>✓ Admin dashboard</li>
            </ul>
            <button
              className="billing-button"
              onClick={() => handleSubscribe('monthly')}
              disabled={checkoutDisabled}
            >
              {loading === 'monthly' ? (
                <span className="billing-spinner" aria-label="Loading" />
              ) : (
                'Start Free Trial'
              )}
            </button>
          </div>

          <div className="billing-card featured">
            <div className="best-value-badge">Best Value</div>
            <h3>Yearly</h3>
            <div className="billing-price">
              <span className="currency">$</span>
              <span className="amount">298</span>
              <span className="period">/year</span>
            </div>
            <p className="savings-text">Save $50/year</p>
            <ul className="billing-features">
              <li>✓ Everything in Monthly</li>
              {/* <li>✓ 2 months free</li> */}
              <li>✓ Priority support</li>
              <li>✓ Early access to features</li>
            </ul>
            <button
              className="billing-button primary"
              onClick={() => handleSubscribe('yearly')}
              disabled={checkoutDisabled}
            >
              {loading === 'yearly' ? (
                <span className="billing-spinner" aria-label="Loading" />
              ) : (
                'Start Free Trial'
              )}
            </button>
          </div>
        </div>

        <p className="billing-note">
          All plans include a 30-day free trial. Cancel anytime, no questions
          asked.
        </p>
      </div>
    </div>
  );
}
